import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useRegisterMutation } from '@/store/api';
import { Button, Field } from '@/components/ui';

// Mirrors the server schema so the caller sees the rule before submitting.
const schema = z.object({
  firstName: z.string().min(1, 'Enter your first name'),
  lastName: z.string().min(1, 'Enter your last name'),
  companyName: z.string().min(1, 'Enter your restaurant or company name'),
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(12, 'Use at least 12 characters')
});

type FormValues = z.infer<typeof schema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const [createAccount, { isLoading, error }] = useRegisterMutation();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    const result = await createAccount(values);
    if (!('error' in result)) navigate('/setup');
  });

  const message =
    error && 'data' in error
      ? ((error.data as { error?: { message?: string } })?.error?.message ??
        'That did not work. Try again.')
      : null;

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
      <h1 className="text-display">Set up your restaurant</h1>
      <p className="mt-1 text-small text-slate">
        Takes about ten minutes. You can add your menu and phone number after this step.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" {...register('firstName')} error={errors.firstName?.message} />
          <Field label="Last name" {...register('lastName')} error={errors.lastName?.message} />
        </div>
        <Field
          label="Restaurant name"
          {...register('companyName')}
          error={errors.companyName?.message}
        />
        <Field label="Email" type="email" {...register('email')} error={errors.email?.message} />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          {...register('password')}
          error={errors.password?.message}
        />

        {message ? (
          <p role="alert" className="rounded bg-rust-soft px-3 py-2 text-small text-rust">
            {message}
          </p>
        ) : null}

        <Button type="submit" loading={isLoading} className="w-full">
          Create account
        </Button>
      </form>

      <p className="mt-6 text-small text-slate">
        Already set up?{' '}
        <Link to="/login" className="text-jade underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </div>
  );
}
